import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Plus, QrCode, ShieldCheck, Copy, Printer, RefreshCw, Download, FileUp, Edit2, Snowflake } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { format } from 'date-fns';
import { id as localeId } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const planColors = {
  Starter: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  Pro: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
  Elite: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  Daily: 'bg-zinc-500/10 text-zinc-300 border-zinc-500/30',
  'All Access': 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30',
};

const statusColors = {
  Active: 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30',
  Expired: 'bg-red-500/10 text-red-400 border-red-500/30',
};

function formatMemberDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return format(parsed, 'd MMM yyyy', { locale: localeId });
}

const formatRupiah = (amount) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(Number(amount || 0));

function formatAccessCategories(categories) {
  const values = Array.isArray(categories) ? categories : [];
  const unique = [];
  for (const value of values) {
    const normalized = String(value || '').trim().toUpperCase();
    if (!normalized || unique.includes(normalized)) continue;
    unique.push(normalized);
  }
  return unique.length ? unique.join(', ') : '-';
}

function normalizePhone(value) {
  const digits = String(value || '').replace(/\D+/g, '');
  if (!digits) return '';
  if (digits.startsWith('0')) return `62${digits.slice(1)}`;
  return digits;
}

function normalizeMemberLookupText(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function getPaymentCategory(description = '') {
  const text = String(description || '').trim().toLowerCase();
  if (text.startsWith('pt package')) return 'PT';
  if (text.startsWith('cardio') || text.includes(' coach ') || text.includes(' tanggal ')) return 'CARDIO';
  if (text.startsWith('gym') || text === 'daily visit') return 'GYM';
  if (text.startsWith('all access')) return 'ALL ACCESS';
  if (text.startsWith('membership')) return 'MEMBERSHIP';
  return 'LAINNYA';
}

function parsePtSessionCountFromDescription(description = '') {
  const text = String(description || '').trim();
  if (!text) return 1;
  const sesiMatch = text.match(/\((\d+)\s*sesi\)/i);
  if (sesiMatch?.[1]) return Math.max(1, Number(sesiMatch[1]));
  const multiplierMatch = text.match(/\b(\d+)\s*x\b/i);
  if (multiplierMatch?.[1]) return Math.max(1, Number(multiplierMatch[1]));
  return 1;
}

function normalizePtSessionStatuses(rawStatuses, totalSessions, legacyStatus = '') {
  const total = Math.max(1, Number(totalSessions || 1));
  const statuses = Array.isArray(rawStatuses)
    ? rawStatuses.map((item) => (String(item || '').trim().toLowerCase() === 'done' ? 'done' : 'not_done'))
    : [];
  const defaultStatus = String(legacyStatus || '').trim().toLowerCase() === 'done' ? 'done' : 'not_done';
  if (statuses.length === 0) {
    return Array.from({ length: total }, () => defaultStatus);
  }
  const filled = [...statuses];
  while (filled.length < total) {
    filled.push('not_done');
  }
  return filled.slice(0, total);
}

function parseCardioClassDate(description = '') {
  const text = String(description || '');
  const match = text.match(/tanggal\s+(\d{4}-\d{2}-\d{2})/i);
  return match?.[1] || '';
}

function isBeforeToday(dateValue) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return target < today;
}

function getPackageStatusBadgeClass(tone = 'active') {
  if (tone === 'expired') return 'border-red-500/30 bg-red-500/10 text-red-300';
  if (tone === 'neutral') return 'border-zinc-500/30 bg-zinc-500/10 text-zinc-300';
  return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300';
}

function isBfPackageCategory(category = '') {
  const normalized = String(category || '').trim().toUpperCase();
  return ['PT', 'CARDIO', 'GYM', 'ALL ACCESS', 'MEMBERSHIP'].includes(normalized);
}

function buildPackageInsight(member, payment) {
  const paymentStatus = String(payment?.status || 'Paid').trim();
  if (paymentStatus && paymentStatus.toLowerCase() !== 'paid') {
    return {
      label: paymentStatus,
      tone: 'neutral',
      note: 'Pembayaran belum final.',
      sessionText: '',
    };
  }

  const category = getPaymentCategory(payment?.description);

  if (category === 'PT') {
    const totalSessions = parsePtSessionCountFromDescription(payment?.description);
    const statuses = normalizePtSessionStatuses(payment?.pt_session_statuses, totalSessions, payment?.session_status);
    const doneCount = statuses.filter((item) => item === 'done').length;
    const remainingSessions = Math.max(totalSessions - doneCount, 0);
    const ptEndDate = payment?.pt_end_date ? new Date(payment.pt_end_date) : null;
    const isDateExpired = ptEndDate && ptEndDate < new Date();
    const isExpired = remainingSessions <= 0 || isDateExpired;
    let note = '';
    if (isExpired && isDateExpired && remainingSessions > 0) {
      note = `Masa aktif habis (${formatMemberDate(payment.pt_end_date)}). Sisa sesi ${remainingSessions}/${totalSessions}.`;
    } else if (isExpired) {
      note = `Semua sesi selesai (${doneCount}/${totalSessions}).`;
    } else if (ptEndDate) {
      note = `Sisa sesi ${remainingSessions} dari ${totalSessions}. Berlaku hingga ${formatMemberDate(payment.pt_end_date)}.`;
    } else {
      note = `Sisa sesi ${remainingSessions} dari ${totalSessions}.`;
    }
    return {
      label: isExpired ? 'Expired' : 'Active',
      tone: isExpired ? 'expired' : 'active',
      note,
      sessionText: `Sesi: ${doneCount}/${totalSessions} selesai`,
    };
  }

  if (category === 'CARDIO') {
    const sessionStatus = String(payment?.session_status || 'not_done').trim().toLowerCase();
    const classDate = parseCardioClassDate(payment?.description);
    if (sessionStatus === 'done') {
      return {
        label: 'Expired',
        tone: 'expired',
        note: 'Sesi cardio sudah selesai digunakan.',
        sessionText: 'Sesi: Selesai',
      };
    }
    if (classDate) {
      const classDateLabel = formatMemberDate(classDate);
      if (isBeforeToday(classDate)) {
        return {
          label: 'Expired',
          tone: 'expired',
          note: `Jadwal kelas ${classDateLabel} sudah lewat.`,
          sessionText: 'Sesi: Belum selesai',
        };
      }
      return {
        label: 'Active',
        tone: 'active',
        note: `Jadwal kelas: ${classDateLabel}.`,
        sessionText: 'Sesi: Belum selesai',
      };
    }
    return {
      label: 'Active',
      tone: 'active',
      note: 'Menunggu sesi cardio dijalankan.',
      sessionText: 'Sesi: Belum selesai',
    };
  }

  const startDate = member?.membership_start_date || member?.join_date;
  const endDate = member?.membership_end_date;
  const endAt = endDate ? new Date(endDate) : null;
  const isExpired = endAt && !Number.isNaN(endAt.getTime()) ? endAt < new Date() : member?.status === 'Expired';
  let periodText = '';
  if (startDate && endDate) {
    periodText = `${formatMemberDate(startDate)} - ${formatMemberDate(endDate)}`;
  } else if (endDate) {
    periodText = `Berlaku sampai ${formatMemberDate(endDate)}`;
  }
  return {
    label: isExpired ? 'Expired' : 'Active',
    tone: isExpired ? 'expired' : 'active',
    note: periodText ? `Masa aktif: ${periodText}` : 'Masa aktif mengikuti paket member.',
    sessionText: '',
  };
}

export default function MemberList() {
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDoorAccessModalOpen, setIsDoorAccessModalOpen] = useState(false);
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);
  const [selectedPackageMember, setSelectedPackageMember] = useState(null);
  const [regeneratingPackageBarcodeId, setRegeneratingPackageBarcodeId] = useState('');
  const [downloadingBarcodePdfMemberId, setDownloadingBarcodePdfMemberId] = useState('');
  const [doorAccessData, setDoorAccessData] = useState(null);
  const [isLoadingDoorAccess, setIsLoadingDoorAccess] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', address: '', membership_start_date: '' });
  const [editingMember, setEditingMember] = useState(null);
  const [importingMembers, setImportingMembers] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const importFileInputRef = useRef(null);
  const [freezeDaysInput, setFreezeDaysInput] = useState(5);
  const [freezingMemberId, setFreezingMemberId] = useState('');
  const [submittingFreeze, setSubmittingFreeze] = useState(false);

  const normalizedFormPhone = normalizePhone(formData.phone);
  const duplicatePhoneMember = members.find((member) => {
    if (!normalizedFormPhone) return false;
    if (editingMember && member.id === editingMember.id) return false;
    return normalizePhone(member.phone) === normalizedFormPhone;
  });
  const hasDuplicatePhone = Boolean(duplicatePhoneMember);

  const planFilterOptions = Array.from(
    new Set([
      ...plans
        .filter((plan) => plan && plan.is_active !== false)
        .map((plan) => String(plan.name || '').trim())
        .filter(Boolean),
      ...members
        .map((member) => String(member.plan_type || '').trim())
        .filter(Boolean),
      ...members
        .flatMap((member) => (Array.isArray(member.access_categories) ? member.access_categories : []))
        .map((category) => String(category || '').trim().toUpperCase())
        .filter(Boolean),
    ])
  ).sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    fetchMembers();
    fetchPlans();
    fetchPayments();
  }, []);

  const fetchMembers = async () => {
    try {
      const { data } = await axios.get(`${API}/members`, { withCredentials: true });
      setMembers(data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const { data } = await axios.get(`${API}/plans`, { withCredentials: true });
      setPlans(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      setPlans([]);
    }
  };

  const fetchPayments = async () => {
    try {
      const { data } = await axios.get(`${API}/payments`, { withCredentials: true });
      setPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      setPayments([]);
    }
  };

  const selectedMemberPayments = useMemo(() => {
    if (!selectedPackageMember?.id) return [];
    const selectedMemberId = String(selectedPackageMember.id);
    const selectedMemberName = normalizeMemberLookupText(selectedPackageMember.name);
    return payments
      .filter((payment) => {
        if (String(payment.member_id || '') === selectedMemberId) return true;

        const paymentMemberName = normalizeMemberLookupText(payment.member_name);
        return selectedMemberName && paymentMemberName === selectedMemberName;
      })
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [payments, selectedPackageMember]);

  const handleOpenPackageDetail = (member) => {
    setSelectedPackageMember(member);
    setIsPackageModalOpen(true);
  };

  const handleRegeneratePackageBarcode = async (paymentId) => {
    const normalizedPaymentId = String(paymentId || '').trim();
    if (!normalizedPaymentId || regeneratingPackageBarcodeId) return;

    try {
      setRegeneratingPackageBarcodeId(normalizedPaymentId);
      await axios.post(
        `${API}/payments/${normalizedPaymentId}/package-barcode/regenerate`,
        {},
        { withCredentials: true }
      );
      await fetchPayments();
    } catch (error) {
      console.error('Failed to regenerate package barcode:', error);
      alert(error.response?.data?.detail || 'Gagal generate ulang barcode paket.');
    } finally {
      setRegeneratingPackageBarcodeId('');
    }
  };

  const handleDownloadPackageBarcodePdf = async (member) => {
    const memberId = String(member?.id || '').trim();
    if (!memberId || downloadingBarcodePdfMemberId) return;

    try {
      setDownloadingBarcodePdfMemberId(memberId);
      const response = await axios.get(`${API}/members/${memberId}/package-barcodes/export-pdf`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = String(member?.name || memberId).replace(/[^a-zA-Z0-9_-]/g, '-');
      link.href = url;
      link.download = `barcode-member-${safeName || memberId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download package barcode PDF:', error);
      alert(error.response?.data?.detail || 'Gagal download PDF barcode paket.');
    } finally {
      setDownloadingBarcodePdfMemberId('');
    }
  };

  const filteredMembers = members.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          String(member.phone || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          String(member.address || '').toLowerCase().includes(searchQuery.toLowerCase());
    const memberCategories = Array.isArray(member.access_categories)
      ? member.access_categories.map((category) => String(category || '').trim().toUpperCase())
      : [];
    const matchesPlan =
      planFilter === 'all' ||
      member.plan_type === planFilter ||
      memberCategories.includes(String(planFilter || '').trim().toUpperCase());
    const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
    return matchesSearch && matchesPlan && matchesStatus;
  });

  const totalMembers = members.length;
  const activeMembers = members.filter((member) => member.status === 'Active').length;
  const expiredMembers = members.filter((member) => member.status === 'Expired').length;
  const thisMonthMembers = members.filter((member) => {
    if (!member.join_date) return false;
    const joinDate = new Date(member.join_date);
    if (Number.isNaN(joinDate.getTime())) return false;
    const now = new Date();
    return joinDate.getMonth() === now.getMonth() && joinDate.getFullYear() === now.getFullYear();
  }).length;

  const handleAddMember = () => {
    setEditingMember(null);
    setFormData({ name: '', phone: '', address: '', membership_start_date: '' });
    setIsModalOpen(true);
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '',
      phone: member.phone || '',
      address: member.address || '',
      membership_start_date: member.membership_start_date ? member.membership_start_date.slice(0, 10) : '',
    });
    setFreezeDaysInput(5);
    setFreezingMemberId('');
    setIsModalOpen(true);
  };

  const handleFreezePT = async (memberId) => {
    if (!freezeDaysInput || freezeDaysInput < 1) {
      alert('Masukkan jumlah hari freeze yang valid.');
      return;
    }
    if (!window.confirm(`Request freeze ${freezeDaysInput} hari untuk member ini?`)) return;
    setSubmittingFreeze(true);
    try {
      await axios.post(`${API}/members/${memberId}/pt-freeze-request`, { freeze_days: Number(freezeDaysInput) }, { withCredentials: true });
      alert('Request freeze berhasil dikirim. Menunggu approval admin.');
      setFreezingMemberId('');
      const { data } = await axios.get(`${API}/members`, { withCredentials: true });
      setMembers(data);
      const updated = data.find((m) => m.id === memberId);
      if (updated) setEditingMember(updated);
    } catch (error) {
      alert(error.response?.data?.detail || 'Gagal mengirim request freeze.');
    } finally {
      setSubmittingFreeze(false);
    }
  };

  const handleDownloadImportTemplate = async () => {
    if (downloadingTemplate) return;
    try {
      setDownloadingTemplate(true);
      const response = await axios.get(`${API}/members/import-template.xlsx`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob(
        [response.data],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template-import-member-list.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download member import template:', error);
      alert(error.response?.data?.detail || 'Gagal download template import member.');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleOpenImportPicker = () => {
    if (importingMembers) return;
    importFileInputRef.current?.click();
  };

  const handleImportMembers = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isXlsx = /\.xlsx$/i.test(String(file.name || ''));
    if (!isXlsx) {
      alert('Format file harus .xlsx');
      event.target.value = '';
      return;
    }

    const formBody = new FormData();
    formBody.append('file', file);

    try {
      setImportingMembers(true);
      const { data } = await axios.post(`${API}/members/import`, formBody, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const summary = data?.summary || {};
      const warningCount = Array.isArray(summary.warnings) ? summary.warnings.length : 0;
      await Promise.all([fetchMembers(), fetchPayments()]);
      alert(
        `Import selesai.\n` +
        `Total baris: ${summary.total_rows || 0}\n` +
        `Diproses: ${summary.processed_rows || 0}\n` +
        `Member baru: ${summary.created_members || 0}\n` +
        `Member diupdate: ${summary.updated_members || 0}\n` +
        `Paket PT dibuat: ${summary.created_pt_payments || 0}\n` +
        `Paket barcode dibuat: ${summary.created_package_payments || 0}\n` +
        `Baris dilewati: ${summary.skipped_rows || 0}\n` +
        `Peringatan: ${warningCount}`
      );
    } catch (error) {
      console.error('Failed to import members from excel:', error);
      alert(error.response?.data?.detail || 'Gagal import data member.');
    } finally {
      setImportingMembers(false);
      event.target.value = '';
    }
  };

  const handleOpenDoorAccess = async (member) => {
    setIsDoorAccessModalOpen(true);
    setDoorAccessData(null);
    setIsLoadingDoorAccess(true);
    try {
      const { data } = await axios.get(`${API}/members/${member.id}/door-access`, { withCredentials: true });
      setDoorAccessData(data);
    } catch (error) {
      console.error('Failed to fetch member attendance QR:', error);
      alert(error.response?.data?.detail || 'Gagal mengambil QR absen member.');
      setIsDoorAccessModalOpen(false);
    } finally {
      setIsLoadingDoorAccess(false);
    }
  };

  const copyQrPayload = async (payload) => {
    try {
      await navigator.clipboard.writeText(payload);
      alert('Kode QR berhasil disalin.');
    } catch (_) {
      alert('Browser tidak mengizinkan copy otomatis.');
    }
  };

  const printDoorQrThermal = ({ memberName, accessScope, doorName, qrPayload, membershipEndDate }) => {
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(qrPayload)}`;
    const expiredLabel = formatMemberDate(membershipEndDate);
    const printWindow = window.open('', '_blank', 'width=420,height=760');
    if (!printWindow) {
      alert('Popup diblokir browser. Izinkan popup untuk print barcode.');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Absen</title>
          <style>
            @page { size: 80mm auto; margin: 4mm; }
            body { font-family: "Courier New", monospace; font-size: 12px; margin: 0; color: #000; }
            .wrap { width: 72mm; margin: 0 auto; text-align: center; }
            .title { font-weight: 700; margin-bottom: 2px; }
            .sub { margin-bottom: 10px; }
            .line { border-top: 1px dashed #000; margin: 8px 0; }
            .meta { text-align: left; font-size: 11px; line-height: 1.45; }
            .qr { width: 56mm; height: 56mm; object-fit: contain; margin: 6px auto; display: block; }
            .payload { word-break: break-all; font-size: 9px; text-align: left; }
          </style>
        </head>
        <body>
          <div class="wrap">
            <div class="title">FITPULSE PRO GYM</div>
            <div class="sub">QR ABSEN MEMBER</div>
            <div class="line"></div>
            <div class="meta">
              <div>Member : ${memberName || '-'}</div>
              <div>Kategori: ${accessScope || '-'}</div>
              <div>QR     : ${doorName || '-'}</div>
              <div>Berlaku: ${expiredLabel}</div>
            </div>
            <div class="line"></div>
            <img class="qr" src="${qrImageUrl}" alt="QR Absen Member" />
            <div class="payload">${qrPayload || '-'}</div>
            <div class="line"></div>
            <div>Gunakan QR ini untuk absen/check-in.</div>
          </div>
          <script>
            (function () {
              var hasPrinted = false;
              var qrImg = document.querySelector('.qr');

              function doPrint() {
                if (hasPrinted) return;
                hasPrinted = true;
                window.focus();
                window.print();
              }

              window.onafterprint = function () {
                window.close();
              };

              // Fallback supaya tetap print walau gambar lambat/gagal dimuat.
              setTimeout(doPrint, 2000);

              if (!qrImg) {
                doPrint();
                return;
              }

              if (qrImg.complete && qrImg.naturalWidth > 0) {
                doPrint();
                return;
              }

              qrImg.onload = doPrint;
              qrImg.onerror = doPrint;
            })();
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEditing = Boolean(editingMember);
    const payload = {
      name: formData.name,
      phone: formData.phone || undefined,
      address: formData.address.trim(),
      membership_start_date: formData.membership_start_date || undefined,
    };
    if (!isEditing) {
      payload.membership_start_date = formData.membership_start_date || null;
    }
    try {
      if (isEditing) {
        await axios.put(
          `${API}/members/${editingMember.id}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API}/members`,
          payload,
          { withCredentials: true }
        );
      }
      setEditingMember(null);
      setIsModalOpen(false);
      fetchMembers();
    } catch (error) {
      console.error('Failed to save member:', error);
      alert(error.response?.data?.detail || 'Failed to save member');
    }
  };

  return (
    <DashboardLayout>
      <Header title="Member List" />
      <div className="p-8" data-testid="members-page">
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-500">Total Member</p>
            <p className="mt-2 text-3xl font-black tracking-tighter text-white">{totalMembers}</p>
          </div>
          <div className="rounded-sm border border-emerald-400/20 bg-[linear-gradient(135deg,rgba(16,185,129,0.08),rgba(9,9,11,0.84))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-500">Member Aktif</p>
            <p className="mt-2 text-3xl font-black tracking-tighter text-emerald-400">{activeMembers}</p>
          </div>
          <div className="rounded-sm border border-red-400/20 bg-[linear-gradient(135deg,rgba(239,68,68,0.08),rgba(9,9,11,0.84))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-500">Member Expired</p>
            <p className="mt-2 text-3xl font-black tracking-tighter text-red-400">{expiredMembers}</p>
          </div>
          <div className="rounded-sm border border-[#D4AF37]/25 bg-[linear-gradient(135deg,rgba(212,175,55,0.12),rgba(9,9,11,0.84))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-500">Gabung Bulan Ini</p>
            <p className="mt-2 text-3xl font-black tracking-tighter text-[#D4AF37]">{thisMonthMembers}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" strokeWidth={1.5} />
            <Input
              type="text"
              placeholder="Search by name, phone, or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-900/50 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-sm"
              data-testid="member-search-input"
            />
          </div>

          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className="w-[150px] bg-zinc-900/50 border-white/10 text-white rounded-sm" data-testid="plan-filter">
              <SelectValue placeholder="Plan Type" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="all">All Plans</SelectItem>
              {planFilterOptions.map((planName) => (
                <SelectItem key={planName} value={planName}>{planName}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] bg-zinc-900/50 border-white/10 text-white rounded-sm" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Expired">Expired</SelectItem>
            </SelectContent>
          </Select>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <input
              ref={importFileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={handleImportMembers}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleDownloadImportTemplate}
              disabled={downloadingTemplate || importingMembers}
              className="rounded-sm border-white/10 bg-white/[0.03] text-white hover:bg-zinc-800"
            >
              <Download className="mr-2 h-4 w-4" strokeWidth={1.8} />
              {downloadingTemplate ? 'Mengunduh...' : 'Template Excel'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleOpenImportPicker}
              disabled={importingMembers || downloadingTemplate}
              className="rounded-sm border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#F3D77A] hover:bg-[#D4AF37]/20"
            >
              <FileUp className="mr-2 h-4 w-4" strokeWidth={1.8} />
              {importingMembers ? 'Importing...' : 'Import Excel'}
            </Button>
            <Button
              onClick={handleAddMember}
              className="bg-[#D4AF37] text-black font-semibold hover:bg-[#C59A27] rounded-sm"
              data-testid="add-member-btn"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
              Add Member
            </Button>
          </div>
        </div>
        <p className="mb-5 text-xs text-zinc-500">
          Import data lama gunakan template Excel. Format tanggal wajib <span className="font-mono">dd/mm/yyyy</span>. Kolom Jumlah Bayar, No Invoice, Nama PT/Coach, Jumlah Done, dan Jumlah Not Done bersifat opsional untuk mencatat riwayat paket.
        </p>

        {/* Members Table */}
        <div className="bg-zinc-900/50 border border-white/10 rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="py-4 text-[12px] font-bold uppercase tracking-[0.1em] text-zinc-500">Member</TableHead>
                <TableHead className="py-4 text-[12px] font-bold uppercase tracking-[0.1em] text-zinc-500">Phone</TableHead>
                <TableHead className="py-4 text-[12px] font-bold uppercase tracking-[0.1em] text-zinc-500">Alamat</TableHead>
                <TableHead className="py-4 text-[12px] font-bold uppercase tracking-[0.1em] text-zinc-500">Plan</TableHead>
                <TableHead className="py-4 text-[12px] font-bold uppercase tracking-[0.1em] text-zinc-500">Join Date</TableHead>
                <TableHead className="py-4 text-[12px] font-bold uppercase tracking-[0.1em] text-zinc-500">Expired</TableHead>
                <TableHead className="py-4 text-[12px] font-bold uppercase tracking-[0.1em] text-zinc-500">Status</TableHead>
                <TableHead className="py-4 text-right text-[12px] font-bold uppercase tracking-[0.1em] text-zinc-500">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredMembers.length > 0 ? (
                filteredMembers.map((member, index) => (
                  <TableRow 
                    key={member.id}
                    className="border-white/5 hover:bg-zinc-900/50 transition-colors"
                    data-testid={`member-row-${index}`}
                  >
                    <TableCell className="py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={member.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=E2E8F0&color=334155`}
                          alt={member.name}
                          className="w-10 h-10 rounded-sm object-cover border border-white/10"
                        />
                        <div>
                          <button
                            type="button"
                            onClick={() => handleOpenPackageDetail(member)}
                            className="text-left text-sm font-medium text-white transition-colors hover:text-[#D4AF37] hover:underline"
                            data-testid={`member-package-detail-${index}`}
                          >
                            {member.name}
                          </button>
                          <p className="mt-1 text-xs text-zinc-500">Klik untuk detail paket</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-4 text-sm font-medium text-zinc-300">{member.phone || '-'}</TableCell>
                    <TableCell className="py-4">
                      {member.address ? (
                        <div className="max-w-[260px] rounded-sm border border-white/10 bg-zinc-950/40 px-3 py-2">
                          <p className="max-h-[42px] overflow-hidden text-sm leading-relaxed text-zinc-300" title={member.address}>
                            {member.address}
                          </p>
                        </div>
                      ) : (
                        <span className="text-sm text-zinc-600">Belum diisi</span>
                      )}
                    </TableCell>
                    <TableCell className="py-4">
                      {Array.isArray(member.access_categories) && member.access_categories.length > 0 ? (
                        <div className="flex max-w-[240px] flex-wrap gap-1.5">
                          {member.access_categories.map((category) => (
                            <Badge
                              key={category}
                              variant="outline"
                              className="rounded-sm border-[#D4AF37]/30 bg-[#D4AF37]/10 text-xs font-medium text-[#D4AF37]"
                            >
                              {category}
                            </Badge>
                          ))}
                        </div>
                      ) : member.plan_type ? (
                        <Badge
                          variant="outline"
                          className={`rounded-sm text-xs font-medium ${planColors[member.plan_type] || 'bg-sky-500/10 text-sky-300 border-sky-500/30'}`}
                        >
                          {member.plan_type}
                        </Badge>
                      ) : (
                        <span className="text-sm text-zinc-500">-</span>
                      )}
                    </TableCell>
                    <TableCell className="py-4 text-sm font-medium text-zinc-300">
                      {formatMemberDate(member.join_date)}
                    </TableCell>
                    <TableCell className="py-4">
                      <div>
                        <p className={`text-sm font-medium ${member.status === 'Expired' ? 'text-red-300' : 'text-zinc-300'}`}>
                          {formatMemberDate(member.membership_end_date)}
                        </p>
                        <p className="mt-1 text-xs uppercase tracking-[0.12em] text-zinc-500">Masa aktif</p>
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <Badge variant="outline" className={`rounded-sm text-xs font-medium ${statusColors[member.status]}`}>
                        {member.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEditMember(member)}
                          className="p-2 rounded-sm hover:bg-zinc-800 transition-colors text-zinc-400 hover:text-white"
                          data-testid={`edit-member-${index}`}
                          title="Edit member"
                        >
                          <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => handleOpenDoorAccess(member)}
                          className="p-2 rounded-sm hover:bg-[#D4AF37]/10 transition-colors text-zinc-400 hover:text-[#D4AF37]"
                          data-testid={`door-access-member-${index}`}
                          title="QR akses pintu"
                        >
                          <QrCode className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-zinc-500 text-sm">
                    No members found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add/Edit Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {editingMember ? 'Edit Member' : 'Add New Member'}
              </DialogTitle>
            </DialogHeader>
            {editingMember && editingMember.pt_freeze_used && !editingMember.pt_freeze_active && !editingMember.pt_freeze_pending && (
              <div className="rounded-sm border border-zinc-500/30 bg-zinc-500/10 text-zinc-400 px-4 py-3 text-sm">
                <div className="flex items-center gap-2 font-semibold">
                  <Snowflake className="h-4 w-4" />
                  Freeze sudah pernah digunakan
                </div>
              </div>
            )}
            {editingMember && (editingMember.pt_freeze_pending || editingMember.pt_freeze_active) && (
              <div className={`rounded-sm border px-4 py-3 text-sm ${editingMember.pt_freeze_active ? 'border-blue-500/30 bg-blue-500/10 text-blue-300' : 'border-amber-500/30 bg-amber-500/10 text-amber-300'}`}>
                <div className="flex items-center gap-2 font-semibold">
                  <Snowflake className="h-4 w-4" />
                  {editingMember.pt_freeze_active ? `Frozen sampai ${formatMemberDate(editingMember.pt_freeze_until)}` : 'Freeze pending — menunggu approval admin'}
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {(() => {
                const isFrozen = editingMember && (editingMember.pt_freeze_pending || editingMember.pt_freeze_active || editingMember.pt_freeze_used);
                return (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Name</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-zinc-800 border-white/10 text-white rounded-sm"
                        disabled={isFrozen}
                        required
                        data-testid="member-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Phone</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="bg-zinc-800 border-white/10 text-white rounded-sm"
                        disabled={isFrozen}
                        data-testid="member-phone-input"
                      />
                      {hasDuplicatePhone && (
                        <p className="text-xs text-red-400">
                          Nomor HP sudah terdaftar atas nama {duplicatePhoneMember?.name}. Gunakan nomor lain.
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Alamat Lengkap Tempat Tinggal</Label>
                      <Textarea
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Contoh: Jl. Sudirman No. 12, RT/RW, Kelurahan, Kecamatan, Kota"
                        className="min-h-[92px] resize-none bg-zinc-800 border-white/10 text-white placeholder:text-zinc-600 rounded-sm"
                        disabled={isFrozen}
                        data-testid="member-address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Tanggal Mulai Plan</Label>
                      <Input
                        type="date"
                        value={formData.membership_start_date}
                        onChange={(e) => setFormData({ ...formData, membership_start_date: e.target.value })}
                        className="bg-zinc-800 border-white/10 text-white rounded-sm"
                        disabled={isFrozen}
                        data-testid="member-plan-start-date-input"
                      />
                      <p className="text-xs text-zinc-500">
                        Bisa diedit kapan pun. Masa aktif akan menyesuaikan plan member saat ini.
                      </p>
                    </div>
                    {editingMember && !editingMember.pt_freeze_pending && !editingMember.pt_freeze_active && !editingMember.pt_freeze_used && (() => {
                      const hasPtAccess = (editingMember.access_categories || []).some(c => String(c).toUpperCase() === 'PT');
                      const ptExpired = editingMember.pt_sessions_expired;
                      if (!hasPtAccess || ptExpired) return null;
                      if (freezingMemberId === editingMember.id) {
                        return (
                          <div className="rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 space-y-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#F3D77A]">Freeze Paket PT</p>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-zinc-400 whitespace-nowrap">Durasi freeze</Label>
                              <Input
                                type="number"
                                min={1}
                                max={30}
                                value={freezeDaysInput}
                                onChange={(e) => setFreezeDaysInput(Number(e.target.value))}
                                className="h-9 w-20 bg-zinc-800 border-white/10 text-white rounded-sm text-center"
                              />
                              <span className="text-xs text-zinc-500">hari</span>
                            </div>
                            <div className="flex gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => setFreezingMemberId('')} className="border-white/10 text-white hover:bg-zinc-800 rounded-sm">Batal</Button>
                              <Button type="button" size="sm" onClick={() => handleFreezePT(editingMember.id)} disabled={submittingFreeze} className="bg-[#D4AF37] text-black font-semibold hover:bg-[#C59A27] rounded-sm">
                                <Snowflake className="mr-1 h-3.5 w-3.5" /> {submittingFreeze ? 'Mengirim...' : 'Kirim Request'}
                              </Button>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <Button type="button" variant="outline" onClick={() => setFreezingMemberId(editingMember.id)} className="w-full border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#F3D77A] hover:bg-[#D4AF37]/10 rounded-sm">
                          <Snowflake className="mr-2 h-4 w-4" /> Freeze Paket PT
                        </Button>
                      );
                    })()}
                  </>
                );
              })()}
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1 border-white/10 text-white hover:bg-zinc-800 rounded-sm">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={(hasDuplicatePhone && !editingMember) || (editingMember && (editingMember.pt_freeze_pending || editingMember.pt_freeze_active))}
                  className="flex-1 bg-[#D4AF37] text-black font-semibold hover:bg-[#C59A27] rounded-sm disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="member-submit-btn"
                >
                  {editingMember ? 'Save Changes' : 'Add Member'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={isPackageModalOpen} onOpenChange={setIsPackageModalOpen}>
          <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden border-white/10 bg-zinc-900 p-0 text-white">
            <DialogHeader className="border-b border-white/10 px-6 py-5">
              <DialogTitle className="text-lg font-semibold">Detail Paket Member</DialogTitle>
              <p className="mt-1 text-sm text-zinc-500">
                Daftar kategori paket yang pernah didaftarkan untuk member ini.
              </p>
            </DialogHeader>
            {selectedPackageMember ? (
              <div className="max-h-[calc(90vh-96px)] overflow-y-auto p-6">
                <div className="rounded-sm border border-white/10 bg-black/20 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                    <img
                      src={selectedPackageMember.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedPackageMember.name)}&background=E2E8F0&color=334155`}
                      alt={selectedPackageMember.name}
                      className="h-14 w-14 rounded-sm border border-white/10 object-cover"
                    />
                    <div>
                      <p className="text-lg font-semibold text-white">{selectedPackageMember.name}</p>
                      <p className="mt-1 text-xs text-zinc-500">{selectedPackageMember.phone || 'Tanpa nomor HP'}</p>
                    </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleDownloadPackageBarcodePdf(selectedPackageMember)}
                      disabled={downloadingBarcodePdfMemberId === selectedPackageMember.id}
                      className="h-9 rounded-sm border-white/10 bg-white/[0.03] px-3 text-xs font-semibold text-white hover:bg-zinc-800 disabled:cursor-wait disabled:opacity-60"
                    >
                      <Download className="mr-1.5 h-4 w-4" />
                      {downloadingBarcodePdfMemberId === selectedPackageMember.id ? 'Menyiapkan PDF...' : 'Download PDF Barcode'}
                    </Button>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(selectedPackageMember.access_categories || []).map((category) => (
                      <Badge key={category} variant="outline" className="rounded-sm border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]">
                        {category}
                      </Badge>
                    ))}
                    {(!selectedPackageMember.access_categories || selectedPackageMember.access_categories.length === 0) && (
                      <span className="text-sm text-zinc-500">Belum ada kategori paket.</span>
                    )}
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  {selectedMemberPayments.length > 0 ? (
                    selectedMemberPayments.map((payment) => {
                      const category = getPaymentCategory(payment.description);
                      const packageInsight = buildPackageInsight(selectedPackageMember, payment);
                      return (
                        <div key={payment.id} className="rounded-sm border border-white/10 bg-zinc-950/50 p-4">
                          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge variant="outline" className="rounded-sm border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]">
                                  {category}
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className={`rounded-sm ${getPackageStatusBadgeClass(packageInsight.tone)}`}
                                >
                                  {packageInsight.label}
                                </Badge>
                              </div>
                              <p className="mt-2 font-semibold text-white">{payment.description || '-'}</p>
                              <p className="mt-1 text-xs text-zinc-500">
                                {payment.transaction_number || payment.id} - {formatMemberDate(payment.created_at)}
                              </p>
                              {packageInsight.note ? (
                                <p className="mt-2 text-xs text-zinc-300">{packageInsight.note}</p>
                              ) : null}
                              {isBfPackageCategory(category) ? (
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                  <div className="relative rounded-sm bg-white p-1">
                                    <img
                                      src={`${API}/payments/${payment.id}/package-barcode.svg`}
                                      alt={`Barcode ${payment.package_barcode || payment.id}`}
                                      className={`h-8 w-28 object-contain ${selectedPackageMember?.pt_freeze_active && category === 'PT' ? 'opacity-30' : ''}`}
                                    />
                                    {selectedPackageMember?.pt_freeze_active && category === 'PT' && (
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="rounded-sm bg-blue-600/90 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">FREEZE</span>
                                      </div>
                                    )}
                                  </div>
                                  <p className="font-mono text-[10px] font-semibold text-zinc-400">
                                    {payment.package_barcode || '-'}
                                  </p>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleRegeneratePackageBarcode(payment.id)}
                                    disabled={Boolean(regeneratingPackageBarcodeId) || (selectedPackageMember?.pt_freeze_active && category === 'PT')}
                                    className="h-7 rounded-sm border-white/10 bg-white/[0.03] px-2 text-[10px] text-white hover:bg-zinc-800"
                                  >
                                    <RefreshCw
                                      className={`mr-1 h-3 w-3 ${regeneratingPackageBarcodeId === payment.id ? 'animate-spin' : ''}`}
                                    />
                                    {regeneratingPackageBarcodeId === payment.id ? 'Memproses...' : 'Generate Ulang Barcode'}
                                  </Button>
                                </div>
                              ) : null}
                            </div>
                            <div className="text-left md:text-right">
                              <p className="text-sm font-semibold text-sky-300">{formatRupiah(payment.amount)}</p>
                              <p className="mt-1 text-xs text-zinc-500">{payment.payment_method || '-'}</p>
                              {packageInsight.sessionText ? (
                                <p className="mt-1 text-xs uppercase tracking-[0.14em] text-[#F3D77A]">
                                  {packageInsight.sessionText}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="rounded-sm border border-dashed border-white/10 bg-black/20 px-4 py-10 text-center">
                      <p className="text-sm font-medium text-white">Belum ada riwayat paket</p>
                      <p className="mt-1 text-sm text-zinc-500">Paket akan muncul setelah member melakukan pembayaran.</p>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        <Dialog open={isDoorAccessModalOpen} onOpenChange={setIsDoorAccessModalOpen}>
          <DialogContent className="max-h-[90vh] max-w-4xl overflow-hidden border-white/10 bg-zinc-900 p-0 text-white">
            <DialogHeader className="border-b border-white/10 px-6 py-5">
              <DialogTitle className="text-lg font-semibold">QR Absen Member</DialogTitle>
              <p className="mt-1 text-sm text-zinc-500">
                QR ini digunakan untuk absen/check-in sesuai paket yang terdaftar.
              </p>
            </DialogHeader>

            {isLoadingDoorAccess ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : doorAccessData?.member ? (
              <div className="max-h-[calc(90vh-92px)] overflow-y-auto p-6">
                <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
                  <div className="space-y-4">
                    <div className="rounded-sm border border-white/10 bg-black/20 p-4">
                      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">Member</p>
                      <div className="flex items-center gap-3">
                      <img
                        src={doorAccessData.member.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(doorAccessData.member.name)}&background=E2E8F0&color=334155`}
                        alt={doorAccessData.member.name}
                        className="h-14 w-14 rounded-sm border border-white/10 object-cover"
                      />
                      <div>
                        <p className="text-lg font-semibold text-white">{doorAccessData.member.name}</p>
                        <p className="mt-1 text-xs text-zinc-500">{doorAccessData.member.phone || 'Tanpa nomor HP'}</p>
                      </div>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-sm border border-[#D4AF37]/25 bg-[#D4AF37]/10 p-4">
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Kategori Paket</p>
                        <p className="mt-2 text-2xl font-semibold text-white">
                          {doorAccessData.door_access?.access_scope_label
                            || formatAccessCategories(doorAccessData.door_access?.access_categories)}
                        </p>
                      </div>
                      <div className="rounded-sm border border-white/10 bg-black/20 p-4">
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Masa Aktif</p>
                        <p className="mt-2 text-base font-semibold text-white">
                          {formatMemberDate(doorAccessData.member.membership_end_date)}
                        </p>
                        <p className="mt-1 text-xs text-zinc-500">
                          Plan: {doorAccessData.door_access?.plan_type || doorAccessData.member?.plan_type || '-'}
                        </p>
                      </div>
                    </div>

                    <div className={`rounded-sm border px-4 py-3 text-sm ${
                  doorAccessData.door_access?.access_enabled
                    ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200'
                    : 'border-red-400/20 bg-red-500/10 text-red-200'
                }`}>
                      <div className="flex items-start gap-2">
                        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.7} />
                        <p>
                          {doorAccessData.door_access?.access_enabled
                            ? doorAccessData.door_access?.note
                            : 'QR absen nonaktif karena status member tidak aktif.'}
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleDownloadPackageBarcodePdf(doorAccessData.member)}
                      disabled={downloadingBarcodePdfMemberId === doorAccessData.member?.id}
                      className="flex w-full items-center justify-center gap-2 border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-3 text-sm font-semibold text-[#F3D77A] transition-colors hover:bg-[#D4AF37]/20 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Download className="h-4 w-4" strokeWidth={1.7} />
                      {downloadingBarcodePdfMemberId === doorAccessData.member?.id
                        ? 'Menyiapkan PDF...'
                        : 'Download PDF Barcode'}
                    </button>
                  </div>

                {doorAccessData.door_access?.access_enabled && doorAccessData.door_access?.allowed_doors?.length ? (
                  <div className="space-y-4">
                    {doorAccessData.door_access.allowed_doors.map((door) => (
                      <div key={door.door_id} className="rounded-sm border border-white/10 bg-zinc-950/50">
                        <div className="p-4">
                          <div className="mb-3">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">QR Absen</p>
                            <h4 className="mt-1 text-base font-semibold text-white">{door.door_name}</h4>
                            <p className="mt-1 text-xs leading-5 text-zinc-500">{door.door_description}</p>
                          </div>
                          <div className="mx-auto flex h-[240px] w-[240px] items-center justify-center bg-white p-[6px]">
                            <img
                              src={`https://api.qrserver.com/v1/create-qr-code/?size=228x228&data=${encodeURIComponent(door.qr_payload)}`}
                              alt={`QR absen ${door.door_name}`}
                              className="h-[228px] w-[228px]"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => copyQrPayload(door.qr_payload)}
                            className="mt-3 flex w-full items-center justify-center gap-2 border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-zinc-300 transition-colors hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
                          >
                            <Copy className="h-4 w-4" strokeWidth={1.6} />
                            Salin Kode QR
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              printDoorQrThermal({
                                memberName: doorAccessData.member?.name,
                                accessScope: doorAccessData.door_access?.access_scope_label
                                  || formatAccessCategories(doorAccessData.door_access?.access_categories),
                                doorName: door.door_name,
                                qrPayload: door.qr_payload,
                                membershipEndDate: doorAccessData.member?.membership_end_date,
                              })
                            }
                            className="mt-2 flex w-full items-center justify-center gap-2 border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-2 text-sm font-medium text-[#F3D77A] transition-colors hover:bg-[#D4AF37]/20"
                          >
                            <Printer className="h-4 w-4" strokeWidth={1.6} />
                            Print Thermal
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-sm border border-dashed border-white/10 bg-black/20 px-4 py-10 text-center">
                    <QrCode className="mx-auto mb-3 h-10 w-10 text-zinc-700" strokeWidth={1.5} />
                    <p className="text-sm font-medium text-white">Tidak ada QR absen aktif</p>
                    <p className="mt-1 text-sm text-zinc-500">Pastikan status member Active dan memiliki plan membership.</p>
                  </div>
                )}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}




